import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    type CreateInvitationFormSchemaValues,
    createInvitationFormSchema,
    useCreateInvitation,
} from 'src/features/userManagement/api/useCreateInvitation';
import { InvitationForm } from 'src/features/userManagement/components/forms/InvitationForm';
import { type Invitation, MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { userManagementMessages } from 'src/features/userManagement/utils';
import { useParking } from 'src/providers/ParkingProvider';
import { type DataTableNewEntryProps, tableMessages } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { mapErrors } from 'src/utils/errors';
import { generalErrorMessages, generalLabelMessages, generalMessages } from 'src/utils/messages';

interface InvitationModalFormProps extends DataTableNewEntryProps<Invitation> {}
function InvitationModalForm({ className, onSubmit: onSubmitCallback }: InvitationModalFormProps) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { toast } = useToast();

    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateInvitation({
        onSuccess: ({ email }) => {
            onOpenChange(false);
            toast({
                title: formatMessage(userManagementMessages.membershipInvitationCreateSuccess),
                description: formatMessage(userManagementMessages.membershipInvitationCreateSuccessDetails, { email }),
            });
            if (onSubmitCallback) {
                onSubmitCallback();
            }
        },
        onError: (err) => {
            if (err?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${err.response.data.detail}`,
                });
            } else if (err?.response?.data) {
                // ValidationError<{ role: MEMBERSHIP_ROLES; email: string; validator_allowed_validation: ALLOWED_VALIDATION | null; validator_allowed_pricing_plan: string | null; }>
                mapErrors(err.response.data, form.setError);
            } else {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: formatMessage(generalErrorMessages.unknownError),
                });
            }
        },
    });

    const form = useForm<CreateInvitationFormSchemaValues>({
        resolver: zodResolver(createInvitationFormSchema),
        defaultValues: {
            email: '',
            guest_reasons: [],
        },
    });

    const onSubmit = (values: CreateInvitationFormSchemaValues) => {
        const submitData = {
            parkingId,
            ...values,
        };
        mutate(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    const selectedRole = useWatch({ control: form.control, name: 'role' });

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogTrigger asChild>
                <Button variant="outline" className={cn('ml-auto', className)}>
                    <FontAwesomeIcon className="text-primary-700" icon={faPlus} />
                    {formatMessage(tableMessages.new, {
                        entry: formatMessage(userManagementMessages.invitation).toLowerCase(),
                    })}
                </Button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {formatMessage(tableMessages.new, {
                            entry: formatMessage(userManagementMessages.invitation).toLowerCase(),
                        })}
                    </DialogTitle>
                    <DialogDescription>{formatMessage(userManagementMessages.newUserDescription)}</DialogDescription>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <InvitationForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button
                        disabled={isPending || (!form.formState.isValid && selectedRole === MEMBERSHIP_ROLES.VALIDATOR)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(generalMessages.send)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { InvitationModalForm };
